import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { useNavigate } from "react-router-dom";
import { BarChart3, DollarSign, Youtube, Lock, Lightbulb, Cpu } from "lucide-react";

function Card({ children, className = "" }) {
  return <div className={`bg-[#111827] border border-slate-800/80 rounded-xl p-5 ${className}`}>{children}</div>;
}

function Bars({ data }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v > 0);
  const max = Math.max(...entries.map(([, v]) => v), 0.000001);
  if (entries.length === 0) return <p className="text-sm text-slate-500">No spend recorded yet.</p>;
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-xs text-slate-400 mb-1"><span>{k}</span><span className="text-slate-300">${v.toFixed(6)}</span></div>
          <div className="w-full bg-slate-800 rounded-full h-2"><div className="bg-violet-500 h-2 rounded-full" style={{ width: `${(v / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function SummaryCards({ costs, channel, pct }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <Card data-testid="cost-month-total">
        <div className="flex items-center gap-2 text-xs text-slate-400 mb-2"><DollarSign className="w-3.5 h-3.5" /> Spend this month ({costs.month})</div>
        <div className="text-3xl font-display font-extrabold text-slate-50">${costs.committed.toFixed(4)}</div>
        {channel && <div className="text-xs text-slate-500 mt-1">of ${costs.monthly_budget} {costs.currency} monthly budget</div>}
      </Card>
      <Card>
        <div className="text-xs text-slate-400 mb-2">Budget used</div>
        <div className="text-3xl font-display font-extrabold text-slate-50">{pct}%</div>
        <div className="w-full bg-slate-800 rounded-full h-2 mt-2"><div className={`h-2 rounded-full ${pct > 80 ? "bg-rose-500" : "bg-violet-500"}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div>
      </Card>
      <Card>
        <div className="text-xs text-slate-400 mb-2">Ledger entries</div>
        <div className="text-3xl font-display font-extrabold text-slate-50">{costs.entries}</div>
        {costs.has_unknown && <div className="text-xs text-amber-400 mt-1">{costs.unknown_count} estimated-only</div>}
      </Card>
    </div>
  );
}

function CostCharts({ costs }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
      <Card data-testid="cost-by-provider"><h3 className="font-display font-semibold text-slate-100 mb-4">Cost by provider</h3><Bars data={costs.by_provider} /></Card>
      <Card data-testid="cost-by-role"><h3 className="font-display font-semibold text-slate-100 mb-4">Cost by role / step</h3><Bars data={costs.by_role} /></Card>
    </div>
  );
}

function PerProjectTable({ rows }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl overflow-hidden mb-8">
      <div className="px-5 py-3 border-b border-slate-800 text-sm font-display font-semibold text-slate-200">Per-project costs</div>
      {rows.length === 0 ? (
        <div className="p-8"><EmptyState title="No costs yet" description="Costs appear once you generate scripts, ideas, narration or renders." /></div>
      ) : (
        <table className="w-full text-sm" data-testid="cost-table">
          <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-800">
            <th className="px-5 py-2.5">Project</th><th className="px-5 py-2.5">Cap</th>
            <th className="px-5 py-2.5">Estimated</th><th className="px-5 py-2.5">Actual</th><th className="px-5 py-2.5">Entries</th>
          </tr></thead>
          <tbody>
            {rows.map(({ p, c }) => (
              <tr key={p.id} className="border-b border-slate-800/50 text-slate-300">
                <td className="px-5 py-3">{p.title}</td>
                <td className="px-5 py-3">${(p.budget_cap || 0).toFixed(2)}</td>
                <td className="px-5 py-3">${(c.total_estimated || 0).toFixed(4)}</td>
                <td className="px-5 py-3">${(c.total_actual || 0).toFixed(4)}{c.has_unknown && <span className="ml-1 text-xs text-amber-400">+unknown</span>}</td>
                <td className="px-5 py-3">{c.entries.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ModelPerformanceTable({ models }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl overflow-hidden mb-8" data-testid="model-performance">
      <div className="px-5 py-3 border-b border-slate-800 text-sm font-display font-semibold text-slate-200 flex items-center gap-2">
        <Cpu className="w-4 h-4 text-violet-300" /> Model performance
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 font-normal">usage telemetry — not a quality guarantee</span>
      </div>
      {models.length === 0 ? (
        <div className="p-8"><EmptyState title="No model usage yet" description="Stats appear after the Director/Autopilot make real model calls." /></div>
      ) : (
        <table className="w-full text-sm" data-testid="model-performance-table">
          <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-800">
            <th className="px-5 py-2.5">Model</th><th className="px-5 py-2.5">Calls</th><th className="px-5 py-2.5">OK / Fail</th>
            <th className="px-5 py-2.5">Fallbacks</th><th className="px-5 py-2.5">Avg latency</th><th className="px-5 py-2.5">Cost</th><th className="px-5 py-2.5">Accept rate</th>
          </tr></thead>
          <tbody>
            {models.map((e) => (
              <tr key={e.id} className="border-b border-slate-800/50 text-slate-300">
                <td className="px-5 py-3"><span className="font-mono text-xs">{e.model_id_or_endpoint}</span></td>
                <td className="px-5 py-3">{e.stats.calls}</td>
                <td className="px-5 py-3">{e.stats.succeeded} / {e.stats.failed}</td>
                <td className="px-5 py-3">{e.stats.fallbacks}</td>
                <td className="px-5 py-3">{e.stats.avg_latency_ms != null ? `${e.stats.avg_latency_ms}ms` : "—"}</td>
                <td className="px-5 py-3">${e.stats.total_cost}</td>
                <td className="px-5 py-3">{e.stats.accept_rate != null ? `${Math.round(e.stats.accept_rate * 100)}% (${e.stats.accepted}/${e.stats.accepted + e.stats.revised})` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function YouTubeAndRevenue({ yt, navigate }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
      <Card data-testid="youtube-metrics">
        <h3 className="font-display font-semibold text-slate-100 mb-4 flex items-center gap-2"><Youtube className="w-4 h-4 text-rose-400" /> YouTube channel stats <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">observed</span></h3>
        {!yt.connected ? (
          <div className="text-center py-6">
            <p className="text-sm text-amber-300 mb-3" data-testid="youtube-not-connected">{yt.message}</p>
            <button onClick={() => navigate("/integrations")} className="text-sm text-violet-300 underline">Connect YouTube</button>
          </div>
        ) : yt.stats ? (
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><div className="text-2xl font-bold text-slate-50">{yt.stats.hidden_subscribers ? "—" : yt.stats.subscriber_count?.toLocaleString()}</div><div className="text-xs text-slate-500">Subscribers</div></div>
            <div><div className="text-2xl font-bold text-slate-50">{yt.stats.view_count?.toLocaleString()}</div><div className="text-xs text-slate-500">Views</div></div>
            <div><div className="text-2xl font-bold text-slate-50">{yt.stats.video_count?.toLocaleString()}</div><div className="text-xs text-slate-500">Videos</div></div>
            <div className="col-span-3 text-xs text-slate-600 mt-2">Fetched {yt.freshness} · {yt.youtube_channel_title}</div>
          </div>
        ) : <p className="text-sm text-rose-300">{yt.error || "Stats unavailable."}</p>}
      </Card>
      <Card data-testid="revenue-section">
        <h3 className="font-display font-semibold text-slate-100 mb-4 flex items-center gap-2"><Lock className="w-4 h-4 text-slate-500" /> Revenue</h3>
        <div className="text-center py-6">
          <p className="text-sm text-slate-400">{yt.revenue.reason}</p>
          <p className="text-xs text-slate-600 mt-2">We never estimate or invent revenue figures.</p>
        </div>
      </Card>
    </div>
  );
}

function Suggestions({ costs }) {
  return (
    <Card data-testid="suggestions">
      <h3 className="font-display font-semibold text-slate-100 mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-violet-300" /> Suggestions <span className="text-xs text-slate-500 font-normal">(data-grounded only)</span></h3>
      {costs.suggestions.length === 0 ? (
        <p className="text-sm text-slate-500">No suggestions — spend is well within budget and pricing is fully reported.</p>
      ) : (
        <ul className="space-y-1.5 text-sm text-slate-300 list-disc pl-5">{costs.suggestions.map((s) => <li key={s}>{s}</li>)}</ul>
      )}
    </Card>
  );
}

export default function AnalyticsCosts() {
  const { selectedChannelId, channels } = useStudio();
  const navigate = useNavigate();
  const [costs, setCosts] = useState(null);
  const [yt, setYt] = useState(null);
  const [rows, setRows] = useState([]);
  const [models, setModels] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const cq = selectedChannelId ? `?channel_id=${selectedChannelId}` : "";
      const [c, y, projs, reg] = await Promise.all([
        api.get(`/analytics/costs${cq}`),
        api.get(`/analytics/youtube${cq}`),
        api.get(`/projects${cq}`),
        api.get(`/registry`),
      ]);
      setCosts(c.data); setYt(y.data);
      setModels((reg.data || []).filter((e) => e.stats && e.stats.calls > 0));
      const perProject = await Promise.all(projs.data.map((p) => api.get(`/projects/${p.id}/costs`).then((r) => ({ p, c: r.data }))));
      setRows(perProject);
    } catch (e) { setError(apiError(e)); }
  }, [selectedChannelId]);
  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!costs) return <LoadingState label="Loading analytics…" />;

  const channel = channels.find((c) => c.id === selectedChannelId);
  const pct = costs.monthly_budget ? Math.round((costs.committed / costs.monthly_budget) * 100) : 0;

  return (
    <div className="animate-in-up">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Production</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Analytics &amp; Costs</h1>
        <p className="text-sm text-slate-400 mt-2">Real spend from the ledger. Observed metrics and AI estimates are labeled separately. No numbers are invented.</p>
      </div>

      <SummaryCards costs={costs} channel={channel} pct={pct} />
      <CostCharts costs={costs} />
      <PerProjectTable rows={rows} />
      <ModelPerformanceTable models={models} />
      <YouTubeAndRevenue yt={yt} navigate={navigate} />
      <Suggestions costs={costs} />
    </div>
  );
}
